const METERS_PER_KILOMETER = 1000;
const METERS_PER_MILE = 1609.344;
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const UTC_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/;
// ---- Money -----------------------------------------------------------------------------------------
/**
 * Render `Money` for the context's locale. The currency's own minor-unit exponent (2 for most, 0 for a
 * zero-decimal currency, 3 for a three-decimal one) comes from `Intl`, so the integer minor units are
 * placed correctly without the caller knowing the currency's precision. The minor units are turned into an
 * exact decimal *string* and handed to `Intl` (which parses a string as an exact mathematical value), so
 * the full `Money` int64 domain formats without precision loss — never via a lossy `Number()` conversion.
 */
export function formatMoney(m, ctx) {
    const exponent = currencyMinorUnits(m.currency);
    const decimal = minorUnitsToDecimalString(m.amountMinor, exponent);
    const nf = new Intl.NumberFormat(ctx.locale, { style: 'currency', currency: m.currency });
    // `Intl.NumberFormat.format` parses a string as an exact mathematical value (ES2023); the lib types
    // predate that, so narrow the call to the string overload the runtime implements.
    return nf.format(decimal);
}
/** The typographic minus sign (U+2212): the same width as the plus and the digits, unlike the keyboard hyphen `Intl` emits. */
export const MINUS_SIGN = '\u2212';
/**
 * Swap the sign `Intl` wrote before a negative figure (the hyphen-minus, U+002D) for the typographic minus ({@link MINUS_SIGN}), wherever
 * the locale put it ("-R$ 4,26" → "−R$ 4,26", "-4,26 €" → "−4,26 €"). A positive figure, and a hyphen that is not a sign (after the
 * digits), are returned unchanged. Presentation only: the amount stays a signed integer in minor units.
 */
export function typographicMinus(formatted) {
    const firstDigit = formatted.search(/\d/);
    if (firstDigit < 0)
        return formatted;
    const sign = formatted.lastIndexOf('-', firstDigit);
    return sign < 0 ? formatted : formatted.slice(0, sign) + MINUS_SIGN + formatted.slice(sign + 1);
}
/** {@link formatMoney} with the typographic minus - what a statement or a balance prints for a negative amount. */
export function formatMoneySigned(m, ctx) {
    return typographicMinus(formatMoney(m, ctx));
}
/** Exact `bigint` minor units → a plain decimal string with `exponent` fraction digits (no float). */
function minorUnitsToDecimalString(amountMinor, exponent) {
    const negative = amountMinor < 0n;
    const magnitude = (negative ? -amountMinor : amountMinor).toString();
    const sign = negative ? '-' : '';
    if (exponent === 0)
        return sign + magnitude;
    const padded = magnitude.padStart(exponent + 1, '0');
    const cut = padded.length - exponent;
    return `${sign}${padded.slice(0, cut)}.${padded.slice(cut)}`;
}
/** The number of minor units a currency uses, per `Intl` (e.g. 2 for USD, 0 for JPY, 3 for BHD). */
export function currencyMinorUnits(currency) {
    return (new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions()
        .maximumFractionDigits ?? 2);
}
// ---- Numbers ---------------------------------------------------------------------------------------
export function formatNumber(value, ctx, opts) {
    return new Intl.NumberFormat(ctx.locale, opts).format(value);
}
/**
 * Parse a locale-formatted number back to a JS number (the parsing side of the boundary). Group and
 * decimal separators are derived from the context's locale via `Intl`, so a comma-decimal locale
 * (`1.234,56`) and a dot-decimal locale (`1,234.56`) both parse. Returns `NaN` for input that is not a
 * number in that locale.
 */
export function parseNumber(input, ctx) {
    const parts = new Intl.NumberFormat(ctx.locale).formatToParts(12345.6);
    const group = parts.find((p) => p.type === 'group')?.value ?? '';
    const decimal = parts.find((p) => p.type === 'decimal')?.value ?? '.';
    let normalized = input.trim();
    if (group)
        normalized = normalized.split(group).join('');
    normalized = normalized.split(decimal).join('.');
    normalized = normalized.replace(/\s/g, '');
    if (normalized === '' || !/^[+-]?\d*\.?\d+$/.test(normalized))
        return Number.NaN;
    return Number(normalized);
}
// ---- Time (store UTC, present in the market timezone — REQ-PLT-015) --------------------------------
function assertUtcInstant(utcInstant) {
    const ms = Date.parse(utcInstant);
    if (!UTC_INSTANT.test(utcInstant) || Number.isNaN(ms)) {
        throw new RangeError(`expected a UTC ISO-8601 instant ending in Z: ${utcInstant}`);
    }
    return ms;
}
/** Present a UTC instant in the context's timezone + locale. The stored value is unchanged (still UTC). */
export function formatInstant(utcInstant, ctx, opts = { dateStyle: 'medium', timeStyle: 'short' }) {
    const ms = assertUtcInstant(utcInstant);
    // ctx.timeZone is authoritative: spread opts first so a caller cannot override the market timezone.
    return new Intl.DateTimeFormat(ctx.locale, { ...opts, timeZone: ctx.timeZone }).format(new Date(ms));
}
/** Just the date part, in the market timezone. */
export function formatDate(utcInstant, ctx) {
    return formatInstant(utcInstant, ctx, { dateStyle: 'medium' });
}
/** Just the time part, in the market timezone. */
export function formatTime(utcInstant, ctx) {
    return formatInstant(utcInstant, ctx, { timeStyle: 'short' });
}
// ---- Distance (store meters — REQ-PLT-017) ---------------------------------------------------------
/** Render a meters distance in the context's display unit (km for metric, miles for imperial). */
export function formatDistance(meters, ctx, maximumFractionDigits = 1) {
    if (!Number.isFinite(meters))
        throw new RangeError(`meters must be finite: ${meters}`);
    const imperial = ctx.measurement === 'imperial';
    const unit = imperial ? 'mile' : 'kilometer';
    const value = meters / (imperial ? METERS_PER_MILE : METERS_PER_KILOMETER);
    return new Intl.NumberFormat(ctx.locale, {
        style: 'unit',
        unit,
        unitDisplay: 'short',
        maximumFractionDigits,
    }).format(value);
}
const METERS_PER_FOOT = 0.3048;
/**
 * D705: a walking-scale distance ("300 m", "980 ft") - under a kilometer it reads in meters (metric) or feet (imperial, to the nearest 10 ft);
 * from a kilometer up it is {@link formatDistance}. The value stays meters; the market's measurement system picks the unit.
 */
export function formatShortDistance(meters, ctx) {
    if (!Number.isFinite(meters))
        throw new RangeError(`meters must be finite: ${meters}`);
    if (Math.abs(meters) >= METERS_PER_KILOMETER)
        return formatDistance(meters, ctx);
    const imperial = ctx.measurement === 'imperial';
    const value = imperial ? Math.round(meters / METERS_PER_FOOT / 10) * 10 : Math.round(meters);
    return new Intl.NumberFormat(ctx.locale, { style: 'unit', unit: imperial ? 'foot' : 'meter', unitDisplay: 'short', maximumFractionDigits: 0 }).format(value);
}
// ---- Duration (store seconds — REQ-PLT-018) --------------------------------------------------------
/**
 * Render a seconds duration for the locale. Chooses hours for durations of an hour or more, otherwise
 * minutes — a compact, locale-aware unit string (the domain value stays seconds).
 */
export function formatDuration(seconds, ctx) {
    if (!Number.isFinite(seconds) || seconds < 0) {
        throw new RangeError(`seconds must be a finite, non-negative value: ${seconds}`);
    }
    if (seconds >= SECONDS_PER_HOUR) {
        const hours = seconds / SECONDS_PER_HOUR;
        return new Intl.NumberFormat(ctx.locale, {
            style: 'unit',
            unit: 'hour',
            unitDisplay: 'short',
            maximumFractionDigits: 1,
        }).format(hours);
    }
    const minutes = Math.round(seconds / SECONDS_PER_MINUTE);
    return new Intl.NumberFormat(ctx.locale, {
        style: 'unit',
        unit: 'minute',
        unitDisplay: 'short',
        maximumFractionDigits: 0,
    }).format(minutes);
}
//# sourceMappingURL=format.js.map